/**
 * Lead conversion events (Meta Pixel + GA4/Google Ads). Fired only on a real
 * successful consultation submit or a real tap on a phone number, never on
 * page load. Every call is safe when the tags are blocked or not loaded.
 */
import { fbTrackLead, fbTrackContact } from '@/lib/analytics/fbpixel';

declare global {
  interface Window { gtag?: (...args: any[]) => void }
}

// Google Ads conversion labels ("AW-…/label"). Create "Consultation lead" and
// "Phone click" conversion actions in Google Ads and paste their labels here;
// until then only the GA4 events below are sent (they can also be imported
// into Google Ads as conversions).
const ADS_LEAD_SEND_TO = 'AW-18131246977/I8MvCKnQvqgdEIG_08VD'; // Consultation lead
const ADS_PHONE_SEND_TO = 'AW-18131246977/kx6KCIaYwqgdEIG_08VD'; // Phone click

function gtagSafe(...args: any[]) {
  try { if (typeof window !== 'undefined' && typeof window.gtag === 'function') window.gtag(...args); } catch { /* ignore */ }
}

export function trackConsultationLead(params: { requestId?: number | string; local: boolean; windows?: string }) {
  const id = params.requestId ? String(params.requestId) : '';
  fbTrackLead({ category: params.local ? 'consultation_local' : 'consultation_out_of_area', eventId: id ? `lead_${id}` : undefined });
  gtagSafe('event', 'generate_lead', {
    form_name: 'consultation',
    lead_area: params.local ? 'local' : 'out_of_area',
    window_count: params.windows || '',
    ...(id ? { lead_id: id } : {}),
  });
  if (ADS_LEAD_SEND_TO) gtagSafe('event', 'conversion', { send_to: ADS_LEAD_SEND_TO, ...(id ? { transaction_id: `lead_${id}` } : {}) });
}

/**
 * One tap on a phone number. `location` = where on the page (header, footer,
 * call-bar, sticky, page_body or a data-track tag); `page` = the path.
 * GA4 `phone_click` (link_location, page_path), Google Ads "Phone click"
 * conversion, Meta `Contact` (content_name = page:location).
 */
export function trackPhoneClick(location: string, page?: string) {
  const path = page ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  fbTrackContact({ method: 'phone', source: path ? `${path}:${location}` : location });
  gtagSafe('event', 'phone_click', { link_location: location, page_path: path });
  if (ADS_PHONE_SEND_TO) gtagSafe('event', 'conversion', { send_to: ADS_PHONE_SEND_TO });
}

/** Click on a "Free Consultation" call-to-action (header, hero strip, sticky bar, home section). */
export function trackConsultCta(location: string) {
  gtagSafe('event', 'consult_cta_click', { cta_location: location });
}
